import React, { useState } from 'react';
import { LINK_TYPE_ICONS, CalendarIcon, LockIcon, TransactionIcon } from './NoteTagIcons';

// Notes created from inside a transaction's Timeline/Documents/Parties tab
// (via NotesPanel, see useNotesPanel.js) carry both `transactionName` and a
// `linkType` for the specific item (milestone, doc, party), with `linkLabel`
// already holding that item's own name (milestone title, filename, party
// name). Rather than showing those as two separate pills plus the reminder
// date (three sections), collapse them into one "{transactionName} –
// {linkLabel}" pill. PROPERTY/CONVERSATION notes, and notes linked directly
// via "Link to Transaction" (no transactionName set — see MyNotesPage.js's
// handleSave), aren't in this set and keep their own single link pill as
// before.
const MERGES_WITH_TRANSACTION_NAME = new Set(['TRANSACTION', 'DOCUMENT', 'PARTY']);

const PencilIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

// "30 SEP 2026" — built manually (not toLocaleDateString) so it's the same
// fixed format for every viewer, rather than following each browser's own
// locale like a native date input does.
function formatReminderDate(dateStr) {
  if (!dateStr) return '';
  // "T00:00:00" (no "Z") — parsed as local midnight, not UTC midnight, so this
  // can't land on the wrong calendar day depending on the viewer's timezone.
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return `${d.getDate()} ${MONTH_ABBR[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Renders one note. `variant="card"` is the My Notes page's standalone
 * card (shows its link tag pill, since the list mixes every link type).
 * `variant="inline"` is the compact row used inside a NotesPanel, where the
 * link is already implied by context, so no link tag is shown.
 */
export default function NoteCard({ note, variant = 'card', onUpdate, onDelete, onNavigate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState(note.text);
  const [draftReminder, setDraftReminder] = useState(note.reminderDate || '');
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraftText(note.text);
    setDraftReminder(note.reminderDate || '');
    setIsEditing(true);
  };

  const cancelEdit = () => setIsEditing(false);

  const saveEdit = async () => {
    if (!draftText.trim() || saving) return;
    setSaving(true);
    try {
      await onUpdate(note.id, { text: draftText.trim(), reminderDate: draftReminder || null });
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this note?')) return;
    await onDelete(note.id);
  };

  const wrapClass = variant === 'inline' ? 'rn-inline-note' : 'rn-note-card';
  const textClass = variant === 'inline' ? 'rn-inline-note-text' : 'rn-note-text';
  const actionsClass = variant === 'inline' ? 'rn-inline-note-actions' : 'rn-note-actions';

  // Navigable only on the My Notes list (variant="card") — the inline variant
  // is already shown in the context it links to (e.g. inside the Timeline tab
  // itself), so there's nowhere useful to navigate from there.
  const canNavigate = variant === 'card' && typeof onNavigate === 'function'
    && note.linkType && note.linkType !== 'NONE'
    && (note.linkType === 'PROPERTY' ? !!note.linkId : !!note.transactionId);
  // Edit/Delete are separate actions inside the same card — stop their clicks
  // from also triggering navigation on whatever pill they're nested near.
  const stopAndRun = (fn) => (e) => { e.stopPropagation(); fn(); };
  // The link pill itself is the only clickable surface (not the whole card,
  // and never the reminder-date pill) — so clicking the note text or its
  // date never navigates, only the pill naming where the note is linked.
  const handlePillClick = canNavigate ? stopAndRun(() => onNavigate(note)) : undefined;

  if (isEditing) {
    return (
      <div className={wrapClass}>
        <div style={{ flex: 1 }}>
          <textarea
            className="rn-inline-composer-textarea"
            style={{ width: '100%', minHeight: 44, fontSize: 12.5, fontFamily: 'inherit', border: '1px solid #E5E7EB', borderRadius: 8, padding: 8, resize: 'vertical' }}
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            autoFocus
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 11, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 6 }}>
              <CalendarIcon /> Reminder
              <input
                type="date"
                value={draftReminder || ''}
                onChange={(e) => setDraftReminder(e.target.value)}
                style={{ fontSize: 11, border: '1px solid #E5E7EB', borderRadius: 6, padding: '3px 6px' }}
              />
            </label>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button type="button" className="rn-cancel-btn" onClick={cancelEdit}>Cancel</button>
              <button type="button" className="rn-save-btn" disabled={!draftText.trim() || saving} onClick={saveEdit}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapClass}>
      <div className="rn-note-top" style={variant === 'inline' ? { display: 'flex', justifyContent: 'space-between', width: '100%', gap: 10 } : undefined}>
        <div>
          <div className={textClass}>{note.text}</div>
          {variant === 'inline' && (
            <div className="rn-inline-note-meta">
              {note.reminderDate && (
                <>
                  <span className="rn-inline-meta-item"><CalendarIcon size={11} /> {formatReminderDate(note.reminderDate)}</span>
                  <span>·</span>
                </>
              )}
              <span className="rn-inline-meta-item"><LockIcon size={11} /> Private · Also visible in My Notes</span>
            </div>
          )}
        </div>
        <div className={actionsClass}>
          <button type="button" className="rn-icon-btn" title="Edit note" onClick={stopAndRun(startEdit)}><PencilIcon /></button>
          <button type="button" className="rn-icon-btn" title="Delete note" onClick={stopAndRun(handleDelete)}><TrashIcon /></button>
        </div>
      </div>
      {variant === 'card' && (
        <div className="rn-note-meta">
          {note.transactionName && MERGES_WITH_TRANSACTION_NAME.has(note.linkType) ? (
            <span
              className={`rn-tag-pill rn-tag-transaction-name${canNavigate ? ' rn-tag-clickable' : ''}`}
              onClick={handlePillClick}
              title={canNavigate ? 'Click to open' : undefined}
            >
              <TransactionIcon /> {note.transactionName} – {note.linkLabel}
            </span>
          ) : note.linkType && note.linkType !== 'NONE' ? (() => {
            const LinkTypeIcon = LINK_TYPE_ICONS[note.linkType];
            return (
              <span
                className={`rn-tag-pill rn-tag-${note.linkType.toLowerCase()}${canNavigate ? ' rn-tag-clickable' : ''}`}
                onClick={handlePillClick}
                title={canNavigate ? 'Click to open' : undefined}
              >
                {LinkTypeIcon && <LinkTypeIcon />} {note.linkLabel}
              </span>
            );
          })() : (
            <span>No link</span>
          )}
          {note.reminderDate && (
            <span className="rn-tag-pill rn-tag-reminder"><CalendarIcon /> {formatReminderDate(note.reminderDate)}</span>
          )}
        </div>
      )}
    </div>
  );
}
